import { Text } from 'blaze-ui/Text/Text'
import Styles from './WizardTour.module.scss'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Close, Play3 } from 'blaze-ui/icons'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useModals } from 'almanac-ui/Modal'
import { VideoModal } from 'components/VideoModal/VideoModal'
import { Props, WIZARD_VIDEO_MAP } from './Types'
import { WizardType } from 'components/Wizards/schemas/Types'
import { useEffect, useMemo, useState } from 'react'
import cx from 'classnames'
import { useWizardTourDismissals } from 'hooks/queries/wizardTourDismissals/useWizardTourDismissals'
import { WizardTourDismissal } from 'types'
import { useCreateWizardTourDismissal } from 'hooks/queries/useCreateWizardTourDismissal'

export const WizardTourThumbnail = ({
  type,
  className,
}: Props & {
  className?: string
}) => {
  const embedId = WIZARD_VIDEO_MAP[type as WizardType]
  const { openModal } = useModals()

  const handlePress = () => {
    if (embedId) openModal(VideoModal, { embedId }, { newStack: true })
  }

  return (
    <button
      aria-label="Video trigger"
      className={cx(Styles.playContainer, className)}
      style={{
        backgroundImage: `url(https://img.youtube.com/vi/${embedId}/hqdefault.jpg)`,
      }}
      onClick={handlePress}
    >
      <div className={Styles.playButton}>
        <Play3 />
      </div>
    </button>
  )
}

export const WizardTour = ({ type }: Props) => {
  const embedId = WIZARD_VIDEO_MAP[type as WizardType]
  const [showTour, setShowTour] = useState(false)
  const { stack } = useModals()
  const { mutate: dismissWizardTour } = useCreateWizardTourDismissal()

  const scheduledPostDismissal = useWizardTourDismissals(type as WizardTourDismissal)
  const isDismissed = scheduledPostDismissal.data?.is_dismissed

  // Get amount of modals open at the time of rendering the wizard tour popup
  const stackLength = useMemo(() => stack.length, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Show wizard tour popup after timeout to account for modal's animation
  useEffect(() => {
    setTimeout(() => setShowTour(true), 120)
  }, [])

  const handleCloseTour = () => {
    setShowTour(false)
    dismissWizardTour(type as WizardTourDismissal)
  }

  if (!embedId || isDismissed) return null

  return (
    <div
      className={cx(Styles.wizardTourCard, {
        [Styles.hide]: stack.length < stackLength,
        [Styles.show]: showTour && stack.length === stackLength,
      })}
    >
      <div className={Styles.header}>
        <Text variant="metadata" color="var(--dark-40)">
          Wizard Tour
        </Text>
        <IconButton icon={Close} variant="tertiary" size="xs" onPress={handleCloseTour} aria-label="close" />
      </div>
      <div className={Styles.body}>
        <Heading level={5}>Learn more about how to use this wizard</Heading>
        <WizardTourThumbnail type={type} />
      </div>
    </div>
  )
}
